import { FocusEvent, useMemo, useRef, useState } from 'react';
import { InputAdornment, TextField } from '@mui/material';
import { formatNumbers, worldCurrencies } from 'utilities';
import NumberFormatWhileTyping from '../NumberFormatWhileTyping';
import { NumberFormattedFieldProps } from './NumberFormattedField.props';

const numberFormat = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
};

const NumberFormattedField = ({
  currencyCode,
  dbErrors = {},
  format,
  formErrors = {},
  id,
  label,
  name,
  onBlur,
  onChange,
  type,
  value,
  currencyAdornment,
  variant = 'outlined',
  ...restProps
}: NumberFormattedFieldProps) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const [wasFocused, setWasFocused] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const errors = useMemo(() => {
    const tmpErrors = [];

    if (formErrors[name]) {
      tmpErrors.push(...formErrors[name]);
    }

    if (dbErrors[name]) {
      tmpErrors.push(formErrors[name]);
    }

    return tmpErrors.length ? tmpErrors.join('. ') : '';
  }, [formErrors, dbErrors, name]);

  const onFocus = () => {
    setWasFocused(true);
    setIsEditing(true);
  };

  const handleOnBlur = (e: FocusEvent<HTMLInputElement>) => {
    setIsEditing(false);
    onBlur?.(e);
  };

  const formatNumber = (number: string) =>
    formatNumbers({
      currencyCode,
      format: { ...numberFormat, style: format === 'currency' ? 'currency' : 'decimal' },
      value: number,
      prefix: null,
      suffix: null,
      allowNegativeValue: null,
      currency: null,
      blankIfNull: null,
      staticCurrency: null,
    });

  const slotProps = {
    input: {
      inputComponent: NumberFormatWhileTyping,
      ...(format === 'currency' && currencyCode && {
        startAdornment: (
          <InputAdornment position="start">
            {worldCurrencies[currencyCode as keyof typeof worldCurrencies]}
          </InputAdornment>
        ),
      }),
    },
  };

  return (
    <TextField
      autoComplete="off"
      error={!isEditing && wasFocused && !!errors}
      helperText={!isEditing && wasFocused && errors}
      id={id}
      slotProps={slotProps}
      variant={variant}
      label={label}
      name={name}
      onBlur={handleOnBlur}
      onChange={onChange}
      onFocus={onFocus}
      ref={inputRef}
      type="text"
      value={formatNumber(value ?? '')}
      {...restProps}
    />
  );
};

export default NumberFormattedField;
